import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Users, CalendarPlus, Stethoscope, type LucideIcon } from 'lucide-react'
import type { Stats } from '@/modules/bd-unificada/types/contacto'
import { TIPOS_CONTACTO } from '@/modules/bd-unificada/types/contacto'

interface StatsCardsProps {
  stats?: Stats
  isLoading?: boolean
}

interface StatCardProps {
  title: string
  value: number | string
  icon: LucideIcon
  subtitle?: string
}

function StatCard({ title, value, icon: Icon, subtitle }: StatCardProps) {
  return (
    <Card className="p-6 border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-blue-500/15 via-blue-500/5 via-50% to-transparent dark:from-blue-800/20 dark:via-blue-900/10 dark:via-50% dark:to-transparent">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <Icon className="h-6 w-6 text-blue-500" />
      </div>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card className="p-6 border border-gray-300 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
        <Skeleton className="h-6 w-6 rounded" />
      </div>
    </Card>
  )
}

function labelTipo(tipo: string): string {
  return TIPOS_CONTACTO.find((t) => t.value === tipo)?.label ?? tipo
}

export function StatsCards({ stats, isLoading }: StatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!stats) return null

  const porTipo = Object.entries(stats.por_tipo || {}).sort((a, b) => b[1] - a[1])

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <StatCard title="Total contactos" value={stats.total_contactos} icon={Users} />
      <StatCard
        title="Nuevos este mes"
        value={stats.nuevos_este_mes}
        icon={CalendarPlus}
        subtitle="Desde el día 1 del mes"
      />
      {porTipo.map(([tipo, count]) => (
        <StatCard key={tipo} title={labelTipo(tipo)} value={count} icon={Stethoscope} />
      ))}
    </div>
  )
}
